if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();

        this.modalContent = this.querySelector(
          '[id^="QuickAddInfo-"]'
        );

        this.currentOpener = null;
        this.currentProductUrl = null;
        this.currentProductData = null;

        this.addEventListener(
          'product-info:loaded',
          ({ target }) => {
            target.addPreProcessCallback(
              this.preprocessHTML.bind(this)
            );
          }
        );
      }

      /* =====================================================
         PAGE LOCK
         ===================================================== */

      lockPageScroll() {
        if (this._aldoScrollLocked) return;

        this._aldoScrollLocked = true;

        this._aldoScrollY =
          window.scrollY ||
          window.pageYOffset ||
          0;

        const scrollbarWidth =
          window.innerWidth -
          document.documentElement.clientWidth;

        this._aldoPreviousBodyStyles = {
          position: document.body.style.position,
          top: document.body.style.top,
          left: document.body.style.left,
          right: document.body.style.right,
          width: document.body.style.width,
          overflow: document.body.style.overflow,
          paddingRight: document.body.style.paddingRight,
        };

        this._aldoPreviousHtmlOverflow =
          document.documentElement.style.overflow;

        document.documentElement.style.overflow =
          'hidden';

        document.body.style.position =
          'fixed';

        document.body.style.top =
          `-${this._aldoScrollY}px`;

        document.body.style.left =
          '0';

        document.body.style.right =
          '0';

        document.body.style.width =
          '100%';

        document.body.style.overflow =
          'hidden';

        if (scrollbarWidth > 0) {
          document.body.style.paddingRight =
            `${scrollbarWidth}px`;
        }
      }

      unlockPageScroll() {
        if (!this._aldoScrollLocked) return;

        const scrollY =
          this._aldoScrollY || 0;

        const old =
          this._aldoPreviousBodyStyles || {};

        document.documentElement.style.overflow =
          this._aldoPreviousHtmlOverflow || '';

        document.body.style.position =
          old.position || '';

        document.body.style.top =
          old.top || '';

        document.body.style.left =
          old.left || '';

        document.body.style.right =
          old.right || '';

        document.body.style.width =
          old.width || '';

        document.body.style.overflow =
          old.overflow || '';

        document.body.style.paddingRight =
          old.paddingRight || '';

        this._aldoScrollLocked = false;

        window.scrollTo(
          0,
          scrollY
        );
      }

      /* =====================================================
         CLOSE
         ===================================================== */

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector(
            'cart-notification'
          ) ||
          document.querySelector(
            'cart-drawer'
          );

        if (cartNotification) {
          cartNotification.setActiveElement(
            this.openedBy
          );
        }

        if (this.modalContent) {
          this.modalContent.innerHTML = '';
        }

        if (preventFocus) {
          this.openedBy = null;
        }

        this.currentOpener = null;
        this.currentProductUrl = null;
        this.currentProductData = null;

        super.hide();

        this.unlockPageScroll();
      }

      /* =====================================================
         OPEN
         ===================================================== */

      show(opener) {
        if (!opener) return;

        this.currentOpener = opener;

        const productUrl =
          opener.getAttribute(
            'data-product-url'
          );

        if (!productUrl) {
          console.error(
            'ALDO Quick Add: product URL missing.'
          );

          return;
        }

        this.lockPageScroll();

        opener.setAttribute(
          'aria-disabled',
          'true'
        );

        opener.classList.add(
          'loading'
        );

        const spinner =
          opener.querySelector(
            '.loading__spinner'
          );

        if (spinner) {
          spinner.classList.remove(
            'hidden'
          );
        }

        this.loadProductIntoModal(
          productUrl
        )
          .then(() => {
            super.show(
              opener
            );
          })

          .catch((error) => {
            this.unlockPageScroll();

            console.error(
              'ALDO Quick Add failed:',
              error
            );
          })

          .finally(() => {
            opener.removeAttribute(
              'aria-disabled'
            );

            opener.classList.remove(
              'loading'
            );

            if (spinner) {
              spinner.classList.add(
                'hidden'
              );
            }
          });
      }

      /* =====================================================
         LOAD PRODUCT INTO SAME MODAL
         Used for initial product and sibling colors.
         ===================================================== */

      async loadProductIntoModal(
        productUrl
      ) {
        if (!productUrl) return;

        this.currentProductUrl =
          this.normalizeProductUrl(
            productUrl
          );

        this.currentProductData =
          await this.fetchProductData(
            productUrl
          );

        const response =
          await fetch(
            productUrl
          );

        if (!response.ok) {
          throw new Error(
            `Quick Add request failed: ${response.status}`
          );
        }

        const responseText =
          await response.text();

        const responseHTML =
          new DOMParser().parseFromString(
            responseText,
            'text/html'
          );

        const productElement =
          responseHTML.querySelector(
            'product-info'
          );

        if (!productElement) {
          throw new Error(
            'Quick Add product-info not found.'
          );
        }

        this.preprocessHTML(
          productElement
        );

        HTMLUpdateUtility.setInnerHTML(
          this.modalContent,
          productElement.outerHTML
        );

        this.buildAldoQuickAddShell();

        this.prepareAldoQuickAdd();

        if (
          window.Shopify &&
          Shopify.PaymentButton
        ) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      }

      /* =====================================================
         PRODUCT DATA / SIZE AVAILABILITY
         ===================================================== */

      async fetchProductData(
        productUrl
      ) {
        try {
          const parsed =
            new URL(
              productUrl,
              window.location.origin
            );

          parsed.search = '';

          let pathname =
            parsed.pathname.replace(
              /\.js$/,
              ''
            );

          if (
            pathname.endsWith('/')
          ) {
            pathname =
              pathname.slice(
                0,
                -1
              );
          }

          const response =
            await fetch(
              `${pathname}.js`,
              {
                headers: {
                  Accept: 'application/json',
                },
              }
            );

          if (!response.ok) {
            return null;
          }

          return await response.json();
        } catch (error) {
          console.warn(
            'ALDO Quick Add: product availability data could not be loaded.',
            error
          );

          return null;
        }
      }

      getSizeOptionPosition() {
        const productData =
          this.currentProductData;

        if (
          !productData ||
          !Array.isArray(
            productData.options
          )
        ) {
          return null;
        }

        const index =
          productData.options.findIndex(
            (option) => {
              const name =
                typeof option === 'string'
                  ? option
                  : option?.name;

              const normalized =
                String(
                  name || ''
                )
                  .replace(/\s+/g, ' ')
                  .trim()
                  .toLowerCase();

              return (
                normalized === 'size' ||
                normalized === 'μέγεθος'
              );
            }
          );

        return index >= 0
          ? index + 1
          : null;
      }

      isSizeValueAvailable(
        sizeValue,
        originalInput
      ) {
        const productData =
          this.currentProductData;

        const sizePosition =
          this.getSizeOptionPosition();

        if (
          productData &&
          Array.isArray(
            productData.variants
          ) &&
          sizePosition
        ) {
          const normalizedSize =
            String(
              sizeValue || ''
            )
              .replace(/\s+/g, ' ')
              .trim()
              .toLowerCase();

          const matchingVariants =
            productData.variants.filter(
              (variant) => {
                const optionValue =
                  variant[
                    `option${sizePosition}`
                  ];

                return (
                  String(
                    optionValue || ''
                  )
                    .replace(/\s+/g, ' ')
                    .trim()
                    .toLowerCase() ===
                  normalizedSize
                );
              }
            );

          if (
            matchingVariants.length
          ) {
            return matchingVariants.some(
              (variant) =>
                Boolean(
                  variant.available
                )
            );
          }
        }

        return !(
          originalInput?.disabled ||
          originalInput?.classList?.contains(
            'is-unavailable'
          ) ||
          originalInput?.getAttribute?.(
            'aria-disabled'
          ) === 'true' ||
          /unavailable/i.test(
            originalInput?.getAttribute?.(
              'aria-label'
            ) || ''
          )
        );
      }

      /* =====================================================
         NORMALIZE URL
         ===================================================== */

      normalizeProductUrl(
        url
      ) {
        if (!url) return '';

        try {
          const parsed =
            new URL(
              url,
              window.location.origin
            );

          return (
            parsed.pathname +
            parsed.search
          );
        } catch (error) {
          return String(url);
        }
      }

      /* =====================================================
         FIND OPTION NAME
         ===================================================== */

      getOptionGroupName(
        group
      ) {
        if (!group) return '';

        const legend =
          group.querySelector(
            ':scope > legend'
          );

        const directLabel =
          group.querySelector(
            ':scope > label'
          );

        return String(
          group.dataset.optionName ||
          group.getAttribute(
            'data-option-name'
          ) ||
          group.getAttribute(
            'data-qa-group'
          ) ||
          legend?.textContent ||
          directLabel?.textContent ||
          ''
        )
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
      }

      isSizeGroup(
        group
      ) {
        const name =
          this.getOptionGroupName(
            group
          );

        return (
          name === 'size' ||
          name.startsWith(
            'size '
          ) ||
          name.startsWith(
            'size/'
          ) ||
          name.startsWith(
            'size /'
          ) ||
          name === 'μέγεθος' ||
          name.startsWith(
            'μέγεθος '
          ) ||
          name.startsWith(
            'μέγεθος/'
          ) ||
          name.startsWith(
            'μέγεθος /'
          )
        );
      }

      isColorGroup(
        group
      ) {
        const name =
          this.getOptionGroupName(
            group
          );

        return (
          name === 'color' ||
          name.startsWith(
            'color '
          ) ||
          name.startsWith(
            'color/'
          ) ||
          name.startsWith(
            'color /'
          ) ||
          name === 'colour' ||
          name.startsWith(
            'colour '
          ) ||
          name.startsWith(
            'colour/'
          ) ||
          name.startsWith(
            'colour /'
          ) ||
          name === 'χρώμα' ||
          name.startsWith(
            'χρώμα '
          ) ||
          name.startsWith(
            'χρώμα/'
          ) ||
          name.startsWith(
            'χρώμα /'
          )
        );
      }

      findSizeGroup(
        root
      ) {
        if (!root) return null;

        const groups =
          root.querySelectorAll(
            [
              'fieldset.product-form__input',
              '.product-form__input',
              '[data-option-name]',
              '[data-qa-group]',
            ].join(',')
          );

        return (
          Array.from(
            groups
          ).find(
            (group) =>
              this.isSizeGroup(
                group
              )
          ) || null
        );
      }

      findColorGroup(
        root
      ) {
        if (!root) return null;

        const groups =
          root.querySelectorAll(
            [
              'fieldset.product-form__input',
              '.product-form__input',
              '.product-color-selector',
              '[data-option-name]',
              '[data-qa-group]',
            ].join(',')
          );

        return (
          Array.from(
            groups
          ).find(
            (group) =>
              this.isColorGroup(
                group
              )
          ) || null
        );
      }

      /* =====================================================
         BUILD SHELL
         ===================================================== */

      buildAldoQuickAddShell() {
        const productInfo =
          this.modalContent?.querySelector(
            'product-info'
          );

        if (!productInfo) return;

        const originalPage =
          productInfo.querySelector(
            ':scope > .page-width'
          ) ||
          productInfo.querySelector(
            '.page-width'
          );

        const infoContainer =
          productInfo.querySelector(
            '.product__info-container'
          );

        if (!originalPage || !infoContainer) {
          return;
        }

        /* --------------------------------------------------
           SHELL
           -------------------------------------------------- */

        const shell =
          document.createElement(
            'div'
          );

        shell.className =
          'aldo-qa-shell';

        /* --------------------------------------------------
           MEDIA
           -------------------------------------------------- */

        const mediaStage =
          document.createElement(
            'div'
          );

        mediaStage.className =
          'aldo-qa-media-stage';

        const sourceImage =
          productInfo.querySelector(
            '.product__media-wrapper .product__media-item:first-child img'
          ) ||
          productInfo.querySelector(
            '.product__media-wrapper img'
          );

        if (sourceImage) {
          const image =
            sourceImage.cloneNode(
              false
            );

          image.className =
            'aldo-qa-media-image';

          image.removeAttribute(
            'id'
          );

          image.removeAttribute(
            'style'
          );

          image.loading =
            'eager';

          image.sizes =
            '(min-width: 750px) 500px, calc(100vw - 32px)';

          mediaStage.appendChild(
            image
          );
        }

        /* --------------------------------------------------
           PANEL
           -------------------------------------------------- */

        const panel =
          document.createElement(
            'div'
          );

        panel.className =
          'aldo-qa-panel';

        const scroll =
          document.createElement(
            'div'
          );

        scroll.className =
          'aldo-qa-scroll';

        const summary =
          document.createElement(
            'div'
          );

        summary.className =
          'aldo-qa-summary';

        const options =
          document.createElement(
            'div'
          );

        options.className =
          'aldo-qa-options';

        const details =
          document.createElement(
            'div'
          );

        details.className =
          'aldo-qa-details';

        const footer =
          document.createElement(
            'div'
          );

        footer.className =
          'aldo-qa-footer';

        /* =================================================
           TITLE CLONE
           ================================================= */

        const title =
          infoContainer.querySelector(
            '.product__title'
          );

        if (title) {
          summary.appendChild(
            title.cloneNode(
              true
            )
          );
        }

        /* =================================================
           PRICE CLONE
           ================================================= */

        const price =
          infoContainer.querySelector(
            '[id^="price-"]'
          );

        if (price) {
          const priceClone =
            price.cloneNode(
              true
            );

          priceClone.removeAttribute(
            'id'
          );

          summary.appendChild(
            priceClone
          );
        }

        /* =================================================
           COLOR PROXY FIRST
           ================================================= */

        const colorGroup =
          this.findColorGroup(
            infoContainer
          );

        if (colorGroup) {
          const colorProxy =
            this.buildColorProxy(
              colorGroup
            );

          if (colorProxy) {
            options.appendChild(
              colorProxy
            );
          }
        }

        /* =================================================
           SIZE PROXY SECOND
           ================================================= */

        const sizeGroup =
          this.findSizeGroup(
            infoContainer
          );

        if (sizeGroup) {
          const sizeProxy =
            this.buildSizeProxy(
              sizeGroup
            );

          if (sizeProxy) {
            options.appendChild(
              sizeProxy
            );
          }
        }

        /* =================================================
           DETAILS
           ================================================= */

        const detailsLink =
          infoContainer.querySelector(
            '.product__view-details'
          ) ||
          productInfo.querySelector(
            '.product__view-details'
          );

        if (detailsLink) {
          details.appendChild(
            detailsLink.cloneNode(
              true
            )
          );
        } else {
          const link =
            document.createElement(
              'a'
            );

          link.className =
            'product__view-details';

          link.href =
            this.currentProductUrl ||
            '#';

          link.textContent =
            'View full details';

          details.appendChild(
            link
          );
        }

        /* =================================================
           ADD TO BAG PROXY
           ================================================= */

        const realSubmit =
          infoContainer.querySelector(
            '.product-form__submit'
          );

        if (realSubmit) {
          const proxySubmit =
            realSubmit.cloneNode(
              true
            );

          proxySubmit.removeAttribute(
            'id'
          );

          proxySubmit.type =
            'button';

          proxySubmit.classList.add(
            'aldo-qa-submit-proxy'
          );

          proxySubmit.disabled =
            realSubmit.disabled;

          proxySubmit.addEventListener(
            'click',
            () => {
              const originalSizeGroup =
                this.findSizeGroup(
                  infoContainer
                );

              if (
                originalSizeGroup &&
                !this.hasSelectedSize(
                  originalSizeGroup
                )
              ) {
                this.showProxySizeError();

                return;
              }

              this.clearProxySizeError();

              realSubmit.click();
            }
          );

          footer.appendChild(
            proxySubmit
          );
        }

        /* =================================================
           ASSEMBLE
           ================================================= */

        scroll.appendChild(
          summary
        );

        scroll.appendChild(
          options
        );

        scroll.appendChild(
          details
        );

        panel.appendChild(
          scroll
        );

        panel.appendChild(
          footer
        );

        shell.appendChild(
          mediaStage
        );

        shell.appendChild(
          panel
        );

        originalPage.classList.add(
          'aldo-qa-original-layout'
        );

        productInfo.appendChild(
          shell
        );
      }

      /* =====================================================
         SIZE PROXY
         ===================================================== */

      buildSizeProxy(
        originalGroup
      ) {
        if (!originalGroup) {
          return null;
        }

        const wrapper =
          document.createElement(
            'div'
          );

        wrapper.className =
          'aldo-qa-size-proxy';

        /* HEADER */

        const header =
          document.createElement(
            'div'
          );

        header.className =
          'aldo-qa-size-header';

        const originalLegend =
          originalGroup.querySelector(
            ':scope > legend'
          );

        const originalLabel =
          originalGroup.querySelector(
            ':scope > label'
          );

        let labelText = 'Size';

        const sourceText =
          originalLegend?.textContent ||
          originalLabel?.textContent ||
          '';

        const cleanedText =
          String(sourceText)
            .replace(
              /size\s*(chart|guide)/ig,
              ''
            )
            .replace(
              /πίνακας\s*μεγεθών/ig,
              ''
            )
            .replace(/\//g, '')
            .replace(/\s+/g, ' ')
            .trim();

        if (cleanedText) {
          labelText =
            cleanedText;
        }

        const title =
          document.createElement(
            'span'
          );

        title.className =
          'aldo-qa-size-title';

        title.textContent =
          labelText;

        header.appendChild(
          title
        );

        wrapper.appendChild(
          header
        );

        /* ERROR */

        const error =
          document.createElement(
            'p'
          );

        error.className =
          'aldo-qa-size-error';

        error.textContent =
          'Please select a size';

        error.hidden =
          true;

        wrapper.appendChild(
          error
        );

        /* GRID */

        const grid =
          document.createElement(
            'div'
          );

        grid.className =
          'aldo-qa-size-grid';

        const radios =
          Array.from(
            originalGroup.querySelectorAll(
              'input[type="radio"]'
            )
          );

        /*
         * Start Quick Add with no size chosen.
         */

        radios.forEach(
          (input) => {
            input.checked =
              false;
          }
        );

        radios.forEach(
          (originalInput, index) => {
            const cell =
              document.createElement(
                'button'
              );

            cell.type =
              'button';

            cell.className =
              'aldo-qa-size-cell';

            cell.dataset.index =
              String(index);

            const label =
              Array.from(
                originalGroup.querySelectorAll(
                  'label'
                )
              ).find(
                (candidate) =>
                  candidate.htmlFor ===
                  originalInput.id
              );

            cell.textContent =
              label?.textContent
                .replace(/\s+/g, ' ')
                .trim() ||
              originalInput.value ||
              '';

            const isAvailable =
              this.isSizeValueAvailable(
                originalInput.value,
                originalInput
              );

            if (!isAvailable) {
              cell.disabled =
                true;

              cell.classList.add(
                'is-unavailable'
              );

              cell.setAttribute(
                'aria-disabled',
                'true'
              );
            }

            cell.addEventListener(
              'click',
              () => {
                if (!isAvailable) {
                  return;
                }

                /*
                 * Real Shopify radio.
                 */

                originalInput.click();

                radios.forEach(
                  (radio) => {
                    radio.checked =
                      radio ===
                      originalInput;
                  }
                );

                grid
                  .querySelectorAll(
                    '.aldo-qa-size-cell'
                  )
                  .forEach(
                    (button) => {
                      button.classList.remove(
                        'is-selected'
                      );
                    }
                  );

                cell.classList.add(
                  'is-selected'
                );

                this.clearProxySizeError();
              }
            );

            grid.appendChild(
              cell
            );
          }
        );

        wrapper.appendChild(
          grid
        );

        return wrapper;
      }

      /* =====================================================
         COLOR PROXY
         ===================================================== */

      buildColorProxy(
        originalGroup
      ) {
        if (!originalGroup) {
          return null;
        }

        const clone =
          originalGroup.cloneNode(
            true
          );

        clone.classList.add(
          'aldo-qa-color-proxy'
        );

        clone.removeAttribute(
          'id'
        );

        /*
         * Avoid duplicate form participation.
         */

        clone
          .querySelectorAll(
            'input'
          )
          .forEach(
            (input) => {
              input.removeAttribute(
                'form'
              );

              input.name =
                `aldo-qa-color-${Math.random()
                  .toString(36)
                  .slice(2)}`;
            }
          );

        const originals =
          Array.from(
            originalGroup.querySelectorAll(
              'input[type="radio"], button, a[href]'
            )
          );

        const proxies =
          Array.from(
            clone.querySelectorAll(
              'input[type="radio"], button, a[href]'
            )
          );

        proxies.forEach(
          (proxy, index) => {
            const original =
              originals[index];

            if (!original) return;

            const targetUrl =
              this.extractProductUrl(
                original
              ) ||
              this.extractCardSiblingUrl(
                index
              );

            proxy.dataset.aldoColorIndex =
              String(index);

            if (targetUrl) {
              proxy.dataset.aldoProductUrl =
                targetUrl;
            }

            /*
             * Fix cloned input IDs.
             */

            if (
              proxy.matches(
                'input'
              )
            ) {
              const oldId =
                proxy.id;

              const newId =
                `${oldId || 'aldo-color'}-proxy-${index}`;

              proxy.id =
                newId;

              const matchingLabel =
                Array.from(
                  clone.querySelectorAll(
                    'label'
                  )
                ).find(
                  (label) =>
                    label.htmlFor ===
                    oldId
                );

              if (matchingLabel) {
                matchingLabel.htmlFor =
                  newId;

                matchingLabel.dataset.aldoColorIndex =
                  String(index);

                if (targetUrl) {
                  matchingLabel.dataset.aldoProductUrl =
                    targetUrl;
                }

                matchingLabel.classList.add(
                  'aldo-qa-color-choice'
                );

                if (
                  this.isOriginalColorSelected(
                    original
                  )
                ) {
                  matchingLabel.classList.add(
                    'is-selected'
                  );
                }
              }
            } else {
              proxy.classList.add(
                'aldo-qa-color-choice'
              );

              if (
                this.isOriginalColorSelected(
                  original
                )
              ) {
                proxy.classList.add(
                  'is-selected'
                );
              }
            }
          }
        );

        clone.addEventListener(
          'click',
          (event) => {
            const clicked =
              event.target.closest(
                '[data-aldo-color-index]'
              );

            if (!clicked) return;

            event.preventDefault();
            event.stopPropagation();

            const index =
              Number(
                clicked.dataset.aldoColorIndex
              );

            const original =
              originals[index];

            const productUrl =
              clicked.dataset.aldoProductUrl ||
              this.extractProductUrl(
                original
              ) ||
              this.extractCardSiblingUrl(
                index
              );

            /*
             * Sibling product:
             * reload entire Quick Add.
             */

            if (
              productUrl &&
              this.normalizeProductUrl(
                productUrl
              ) !==
                this.normalizeProductUrl(
                  this.currentProductUrl
                )
            ) {
              this.setModalLoading(
                true
              );

              this.loadProductIntoModal(
                productUrl
              )
                .catch(
                  (error) => {
                    console.error(
                      'ALDO Quick Add color switch failed:',
                      error
                    );
                  }
                )
                .finally(
                  () => {
                    this.setModalLoading(
                      false
                    );
                  }
                );

              return;
            }

            /*
             * True Shopify variant color fallback.
             */

            if (original) {
              original.click();

              setTimeout(
                () => {
                  this.syncDisplayedImageFromOriginal();
                },
                150
              );
            }

            clone
              .querySelectorAll(
                '.aldo-qa-color-choice'
              )
              .forEach(
                (choice) => {
                  choice.classList.remove(
                    'is-selected'
                  );
                }
              );

            const selectedChoice =
              clicked.matches(
                '.aldo-qa-color-choice'
              )
                ? clicked
                : clicked.closest(
                    '.aldo-qa-color-choice'
                  );

            selectedChoice?.classList.add(
              'is-selected'
            );
          }
        );

        return clone;
      }

      /* =====================================================
         COLOR HELPERS
         ===================================================== */

      isOriginalColorSelected(
        element
      ) {
        if (!element) return false;

        if (
          element.matches(
            'input[type="radio"]'
          )
        ) {
          return element.checked;
        }

        if (
          element.getAttribute(
            'aria-checked'
          ) === 'true'
        ) {
          return true;
        }

        return Boolean(
          element.closest(
            '.is-checked, .selected, .active, [data-swatch-active="true"]'
          )
        );
      }

      extractProductUrl(
        element
      ) {
        if (!element) return null;

        const candidates = [
          element.dataset?.productUrl,
          element.dataset?.url,
          element.getAttribute?.(
            'href'
          ),
          element.closest?.(
            '[data-product-url]'
          )?.dataset?.productUrl,
          element.closest?.(
            'a[href]'
          )?.getAttribute?.(
            'href'
          ),
        ];

        for (
          const candidate of candidates
        ) {
          if (
            candidate &&
            String(candidate).includes(
              '/products/'
            )
          ) {
            return candidate;
          }
        }

        return null;
      }

      extractCardSiblingUrl(
        index
      ) {
        const card =
          this.currentOpener?.closest(
            '.product-card-wrapper'
          );

        if (!card) return null;

        const siblings =
          Array.from(
            card.querySelectorAll(
              '.js-card-sibling-swatch[data-product-url]'
            )
          );

        return (
          siblings[index]
            ?.dataset
            ?.productUrl ||
          null
        );
      }

      /* =====================================================
         SIZE STATE
         ===================================================== */

      hasSelectedSize(
        group
      ) {
        if (!group) return true;

        const radios =
          group.querySelectorAll(
            'input[type="radio"]'
          );

        if (radios.length) {
          return Array.from(
            radios
          ).some(
            (input) =>
              input.checked &&
              !input.disabled
          );
        }

        const select =
          group.querySelector(
            'select'
          );

        if (select) {
          return Boolean(
            select.value
          );
        }

        return true;
      }

      showProxySizeError() {
        const wrapper =
          this.modalContent.querySelector(
            '.aldo-qa-size-proxy'
          );

        const error =
          this.modalContent.querySelector(
            '.aldo-qa-size-error'
          );

        const submit =
          this.modalContent.querySelector(
            '.aldo-qa-submit-proxy'
          );

        wrapper?.classList.add(
          'has-error'
        );

        if (error) {
          error.hidden =
            false;
        }

        submit?.classList.add(
          'aldo-size-error-button'
        );
      }

      clearProxySizeError() {
        const wrapper =
          this.modalContent.querySelector(
            '.aldo-qa-size-proxy'
          );

        const error =
          this.modalContent.querySelector(
            '.aldo-qa-size-error'
          );

        const submit =
          this.modalContent.querySelector(
            '.aldo-qa-submit-proxy'
          );

        wrapper?.classList.remove(
          'has-error'
        );

        if (error) {
          error.hidden =
            true;
        }

        submit?.classList.remove(
          'aldo-size-error-button'
        );
      }

      /* =====================================================
         SYNC IMAGE FOR TRUE VARIANT COLOR
         ===================================================== */

      syncDisplayedImageFromOriginal() {
        const productInfo =
          this.modalContent.querySelector(
            'product-info'
          );

        if (!productInfo) return;

        const originalImage =
          productInfo.querySelector(
            '.aldo-qa-original-layout .product__media-item.is-active img'
          ) ||
          productInfo.querySelector(
            '.aldo-qa-original-layout .product__media-item:first-child img'
          );

        const shellImage =
          this.modalContent.querySelector(
            '.aldo-qa-media-image'
          );

        if (
          !originalImage ||
          !shellImage
        ) {
          return;
        }

        shellImage.src =
          originalImage.src;

        shellImage.srcset =
          originalImage.srcset;

        shellImage.alt =
          originalImage.alt;
      }

      /* =====================================================
         LOADING STATE
         ===================================================== */

      setModalLoading(
        loading
      ) {
        const shell =
          this.modalContent.querySelector(
            '.aldo-qa-shell'
          );

        if (!shell) return;

        shell.classList.toggle(
          'is-loading',
          Boolean(loading)
        );
      }

      /* =====================================================
         PREPARE
         ===================================================== */

      prepareAldoQuickAdd() {
        /*
         * Real Shopify controls remain untouched
         * except Size starts visually unselected.
         */

        const productInfo =
          this.modalContent.querySelector(
            'product-info'
          );

        if (!productInfo) return;

        const sizeGroup =
          this.findSizeGroup(
            productInfo.querySelector(
              '.aldo-qa-original-layout'
            )
          );

        if (sizeGroup) {
          sizeGroup
            .querySelectorAll(
              'input[type="radio"]'
            )
            .forEach(
              (input) => {
                input.checked =
                  false;
              }
            );
        }
      }

      /* =====================================================
         SHOPIFY PREPROCESS
         ===================================================== */

      preprocessHTML(
        productElement
      ) {
        if (!productElement) return;

        productElement.classList.forEach(
          (classApplied) => {
            if (
              classApplied.startsWith(
                'color-'
              ) ||
              classApplied ===
                'gradient'
            ) {
              this.modalContent.classList.add(
                classApplied
              );
            }
          }
        );

        this.preventDuplicatedIDs(
          productElement
        );

        this.removeDOMElements(
          productElement
        );

        this.removeGalleryListSemantic(
          productElement
        );

        this.updateImageSizes(
          productElement
        );

        this.preventVariantURLSwitching(
          productElement
        );
      }

      preventVariantURLSwitching(
        productElement
      ) {
        productElement.setAttribute(
          'data-update-url',
          'false'
        );
      }

      removeDOMElements(
        productElement
      ) {
        const pickup =
          productElement.querySelector(
            'pickup-availability'
          );

        if (pickup) {
          pickup.remove();
        }

        const productModal =
          productElement.querySelector(
            'product-modal'
          );

        if (productModal) {
          productModal.remove();
        }

        /*
         * Remove PDP popup dialogs from the detached
         * Quick Add copy only.
         */
        productElement
          .querySelectorAll(
            [
              'modal-dialog',
              '.product-popup-modal',
              '.product-popup-modal__content',
              '.product-popup-modal__content-info'
            ].join(',')
          )
          .forEach(
            (element) => {
              element.remove();
            }
          );

        /*
         * Remove PDP popup openers from the detached
         * Quick Add copy only.
         */
        productElement
          .querySelectorAll(
            [
              'modal-opener[data-modal*="PopupModal"]',
              '.product-popup-modal__opener'
            ].join(',')
          )
          .forEach(
            (element) => {
              element.remove();
            }
          );

        /*
         * Extra protection for standalone Size Guide /
         * Size Chart controls inside the fetched copy.
         */
        productElement
          .querySelectorAll(
            'a, button'
          )
          .forEach(
            (element) => {
              const text =
                String(
                  element.textContent || ''
                )
                  .replace(/\s+/g, ' ')
                  .trim()
                  .toLowerCase();

              if (
                text === 'size chart' ||
                text === 'size guide' ||
                text === 'πίνακας μεγεθών'
              ) {
                element.remove();
              }
            }
          );
      }

      preventDuplicatedIDs(
        productElement
      ) {
        const sectionId =
          productElement.dataset.section;

        if (!sectionId) return;

        const newId =
          `quickadd-${sectionId}`;

        productElement.innerHTML =
          productElement.innerHTML.replaceAll(
            sectionId,
            newId
          );

        Array.from(
          productElement.attributes
        ).forEach(
          (attribute) => {
            if (
              attribute.value.includes(
                sectionId
              )
            ) {
              productElement.setAttribute(
                attribute.name,
                attribute.value.replace(
                  sectionId,
                  newId
                )
              );
            }
          }
        );

        productElement.dataset.originalSection =
          sectionId;
      }

      removeGalleryListSemantic(
        productElement
      ) {
        const gallery =
          productElement.querySelector(
            '[id^="Slider-Gallery"]'
          );

        if (!gallery) return;

        gallery.setAttribute(
          'role',
          'presentation'
        );

        gallery
          .querySelectorAll(
            '[id^="Slide-"]'
          )
          .forEach(
            (item) => {
              item.setAttribute(
                'role',
                'presentation'
              );
            }
          );
      }

      updateImageSizes(
        productElement
      ) {
        productElement
          .querySelectorAll(
            '.product__media img'
          )
          .forEach(
            (image) => {
              image.setAttribute(
                'sizes',
                '(min-width: 750px) 500px, calc(100vw - 32px)'
              );
            }
          );
      }
    }
  );
}


/* =========================================================
   QUICK ADD TRIGGER
   ========================================================= */

if (!window.__aldoQuickAddTriggerBound) {
  window.__aldoQuickAddTriggerBound =
    true;

  document.addEventListener(
    'click',
    function (event) {
      const trigger =
        event.target.closest(
          '.aldo-quick-add-trigger'
        );

      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const opener =
        trigger.closest(
          'modal-opener'
        );

      if (!opener) return;

      const selector =
        opener.getAttribute(
          'data-modal'
        );

      if (!selector) return;

      const modal =
        document.querySelector(
          selector
        );

      if (
        modal &&
        typeof modal.show ===
          'function'
      ) {
        modal.show(
          trigger
        );
      }
    },
    true
  );
}